<template>
  <!--
    CONFIRMAR COMPRA/VENDA, em papel (D0, D15) — era o último modal azul-gradiente do jogo, com
    emojis nadando e cantos desenhados em CSS. É modal de verdade pela regra do D11: gasta dinheiro.
    O contrato (props/emits) é o MESMO da versão antiga de propósito: as seis lojas o usam e estão
    no working tree de outra sessão — este arquivo troca de pele sem obrigar ninguém a acompanhar.

    O valor base do peixe agora sai de `shop.currentPrices` (o bestiário do V2), não da tabela de
    raridade hardcoded da versão antiga — que podia divergir do servidor e mentir o bônus.
  -->
  <FishModal
    :aberto="show"
    :titulo="isSale ? $t('purchase.modal.titleSale') : $t('purchase.modal.title')"
    :fechavel="!isLoading"
    @fechar="cancel"
  >
    <div v-if="item" class="compra__ficha">
      <img class="compra__arte" :src="imagemDoItem" :alt="translatedName || ''">
      <div class="compra__quem">
        <p class="compra__nome">{{ translatedName }}</p>
        <p v-if="translatedDescription" class="compra__descricao">{{ translatedDescription }}</p>
      </div>
    </div>

    <ul class="compra__medidas">
      <li>
        <span>{{ $t('purchase.modal.quantity') }}</span>
        <strong class="t-numero">{{ quantity }}×</strong>
      </li>
      <li>
        <span>{{ $t('purchase.modal.total') }}</span>
        <strong class="t-numero compra__total">
          <FishIcon :nome="isPremium ? 'gem' : 'coins'" tamanho="miudo" />
          {{ formato(totalPrice) }}
        </strong>
      </li>
      <li v-if="valorBase">
        <span>{{ $t('purchase.modal.baseValue') }}</span>
        <strong class="t-numero compra__base">
          {{ formato(valorBase) }}
          <em v-if="percentualDeBonus > 0" class="compra__bonus">
            +{{ percentualDeBonus }}% {{ $t('purchase.modal.bonus') }}
          </em>
        </strong>
      </li>
    </ul>

    <template #acoes>
      <!-- `som=""`: o confirm() já toca o som da transação (compra, venda ou premium). -->
      <FishButton variante="primario" bloco som="" :carregando="isLoading" @click="confirm">
        {{ isLoading ? $t('purchase.modal.processing') : $t('purchase.modal.confirm') }}
      </FishButton>
      <FishButton variante="neutro" bloco :desabilitado="isLoading" @click="cancel">
        {{ $t('purchase.modal.cancel') }}
      </FishButton>
    </template>
  </FishModal>
</template>

<script>
import { getTranslation } from '@/i18n/utils/translations';
import { FishModal, FishButton, FishIcon } from '@/shared/ui';
import { getFishImage } from '@/shared/utils/fishesImages';

export default {
  name: 'PurchaseConfirmModal',
  components: { FishModal, FishButton, FishIcon },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    quantity: {
      type: Number,
      default: 1
    },
    totalPrice: {
      type: Number,
      default: 0
    },
    isPremium: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isSale: {
      type: Boolean,
      default: false
    }
  },
  emits: ['confirm', 'cancel'],
  computed: {
    translatedName() {
      const currentLocale = this.$i18n.locale;

      // Peixe (tem peso) sai da tabela de peixes; item comum, da tabela de itens.
      if (this.item.weight) {
        return this.$t(`fishes.${this.item.name}`);
      }
      if (currentLocale === 'pt') return this.item.name;
      return getTranslation(this.item.name, currentLocale, 'name') || this.item.name;
    },
    translatedDescription() {
      const currentLocale = this.$i18n.locale;
      if (currentLocale === 'pt') return this.item.description;
      return getTranslation(this.item.name, currentLocale, 'description') || this.item.description;
    },
    imagemDoItem() {
      /*
       * `getFishImage` e não o caminho montado à mão. Montar minúscula + hífen não tira
       * acento: para "Tilápia" pedia `tilápia.png`, e o arquivo é `tilapia.png` — a
       * confirmação de venda já abria sem imagem. Os peixes das Ilhas Cazu e da Costa
       * Dourada trazem mais quatro acentuados (Truta-Ártica, Rêmora-Âncora,
       * Tubarão-Duende, Esturjão-Beluga), e a tabela que resolve isso já existe.
       */
      if (this.item.type === 'fish' || this.item.weight) {
        return getFishImage(this.item.name);
      }
      return this.item.image || `/img/items/${this.item.type || 'generic'}/${this.item.name.toLowerCase().replace(/\s+/g, '_')}.png`;
    },
    /**
     * Valor de tabela do peixe vendido: preço base do bestiário × peso. Só aparece na venda,
     * e só quando o mercado já carregou — melhor linha nenhuma que número inventado.
     */
    valorBase() {
      if (!this.isSale || !this.item.weight) return 0;
      const preco = this.$store.state.shop.currentPrices?.[this.item.name]?.basePrice;
      if (!preco) return 0;
      return Math.floor(preco * this.item.weight);
    },
    percentualDeBonus() {
      if (!this.valorBase || this.totalPrice <= this.valorBase) return 0;
      return Math.round((this.totalPrice / this.valorBase - 1) * 100);
    }
  },
  methods: {
    formato(n) {
      return new Intl.NumberFormat(this.$i18n.locale).format(n || 0);
    },
    async confirm() {
      if (this.isSale) {
        await this.$store.dispatch('SoundController/playSound', { category: 'shop', type: 'sell' });
      } else if (this.isPremium) {
        await this.$store.dispatch('SoundController/playSound', { category: 'shop', type: 'buyPremiumItem' });
      } else if (Math.random() < 0.05) {
        // O easter egg de 5% da versão antiga fica: uma compra em vinte soa diferente.
        await this.$store.dispatch('SoundController/playSound', { category: 'shop', type: 'changeBuy' });
      } else {
        await this.$store.dispatch('SoundController/playSound', { category: 'shop', type: 'buy' });
      }

      this.$emit('confirm');
      await this.$store.dispatch('rewards/checkAndShowRewards', this.$store.state.player.id);
    },
    cancel() {
      if (this.isLoading) return;
      this.$emit('cancel');
    }
  }
}
</script>

<style scoped>
.compra__ficha {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  margin-bottom: var(--e-3);
}

/* O retrato do item no papel rebaixado — o mesmo encaixe visual dos slots. */
.compra__arte {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  object-fit: contain;
  background: var(--papel-frio);
  border: 2px solid var(--madeira);
  padding: var(--e-1);
}

.compra__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  color: var(--tinta);
}

.compra__descricao {
  margin: var(--e-1) 0 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.compra__medidas {
  list-style: none;
  margin: 0;
  padding: var(--e-2) 0 0;
  border-top: 2px dashed var(--papel-sombra);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

.compra__medidas li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--e-3);
}

.compra__medidas strong {
  color: var(--tinta);
}

.compra__total {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  color: var(--latao);
  font-size: var(--f-3);
}

.compra__base {
  display: inline-flex;
  align-items: baseline;
  gap: var(--e-2);
}

.compra__bonus {
  font-style: normal;
  font-size: var(--f-1);
  color: var(--latao);
}
</style>
